import { FoodHeader } from '@cf-shared/data-model/food';
import { DateTime } from 'luxon';
import { rankFoodHeaders } from '@cf-shared/food-search/food-search-ranking';
import { rankScannedFoodCandidates, scannedFoodWords, scanWordCompleter } from '@cf-shared/food-search/scanned-food-rank';
import { stapleDeviceRow } from 'services/staple-db-snapshot';
import { stapleRowsToAdd } from '@cf-shared/food-search/staple-merge';

/** The searches and settings a food scanner matches its identified foods with. */
export interface ScannedFoodMatchDeps {
  /** the foods on this device whose names match the text */
  searchDevice: (text: string) => Promise<FoodHeader[]>;
  /** the server's foods for the text, at most `maxCount`; a connection failure answers [] */
  searchServer: (text: string, maxCount: number) => Promise<FoodHeader[]>;
  /** the current time, in UTC; recently logged foods rank higher, as on the Foods page */
  now: DateTime;
  /**
   * The curated staples this device holds, for a scan the server could not answer (food staples
   * plan 4c, #5625, Requirement C6).
   *
   * WHY IT IS A SEPARATE DEPENDENCY rather than something `searchDevice` returns. The device and
   * server searches are launched TOGETHER, so when `searchDevice` runs the server outcome is not
   * known yet, and awaiting it there would make the two run one after the other. This is called
   * after both have answered, and only when the server returned nothing — so the cost of walking
   * every staple is paid only on a scan that found nothing anyway.
   *
   * A zero-result server answer and an unreachable server look the same from here (`searchServer`
   * answers `[]` for both), so the staples are consulted in both cases. That is the right way
   * round: offering a curated food where the server had nothing is no worse than offering
   * nothing, and the alternative is missing the offline case this exists for.
   *
   * Left out, nothing changes — every scan behaves exactly as it did before.
   */
  stapleFallbackHeaders?: () => FoodHeader[];
  /** the staples' names and aliases already split and stemmed, so the fallback does not redo it */
  stapleWords?: ReadonlyMap<string, { doc: unknown; name: string[]; aliases: string[][] }>;
}

/**
 * The device's foods with the staples it holds added, merged into any the device already found.
 *
 * Exported for its unit tests: what it does to the LIST cannot be seen through
 * `matchScannedFoodAsync`, which answers with one food — when the person's row and the staple tie,
 * their row wins either way, so a duplicate in the pool is invisible from outside.
 *
 * The rule itself is shared with the Foods page's offline search (`shared/src/food-search/staple-merge.ts`), so
 * the two cannot drift: their row is kept because it carries the history the ranking weights by,
 * and the curated values are merged ONTO it rather than replacing it.
 */
export function withStapleFallback(deviceFoods: FoodHeader[], staples: FoodHeader[]): FoodHeader[] {
  const added = stapleRowsToAdd(deviceFoods, staples, { buildRow: stapleDeviceRow });
  // hand the caller's own list back when there is nothing to add, so a scan that needed no fallback
  // ranks exactly the array it would have without this
  return added.length === 0 ? deviceFoods : [...deviceFoods, ...added];
}
/** How many server results a ranked scanner match reads per search: the number the Foods page lists. */
export const RANKED_SERVER_RESULTS = 50;

/**
 * The database food a scanner fills in for a food name the AI returned, or null when nothing
 * matches well enough (the user then searches by hand).
 */
export async function matchScannedFoodAsync(aiName: string, deps: ScannedFoodMatchDeps): Promise<FoodHeader | null> {
  // pick from the Foods page's search with the scanner's own rule (#5590)
  return findRankedFoodMatchAsync(aiName, deps);
}

/**
 * The food a scanner fills in for the AI's name, or null when no candidate names the food (#5590).
 *
 * Why not simply the Foods page's first result: that list puts the foods answering the most typed
 * words first, whatever their source, which suits a person scrolling it but not an automatic pick.
 * Measured on 549 real scanned names (2026-09-17, dev index, graded blind), that order picked a
 * wrong food for 84 of them — mostly branded dishes whose long names happen to hold every word
 * ("roasted sweet potato" -> "ROASTED SWEET POTATO WRAP") — against 127 for the older matcher and
 * 68 for this rule. The full measurement is in design-docs/meal-photo-scanner.md.
 *
 * The rule:
 * - Search twice: the whole name, and only its food words (`scannedFoodWords`). "cooked cassava"
 *   alone returns 50 other cooked foods and never the plain cassava.
 * - Order the results with `rankScannedFoodCandidates` (`shared/src/food-search/scanned-food-rank.ts`,
 *   where the scoring is described) and take the first. That function is shared with the server,
 *   which shows its first few candidates to the AI model instead (#5623).
 */
async function findRankedFoodMatchAsync(aiName: string, deps: ScannedFoodMatchDeps): Promise<FoodHeader | null> {
  const text = aiName.trim();
  // every word the AI sent is finished, so the last one is not treated as still being typed
  const completedQuery = scanWordCompleter.searchTextQuery(text, scanWordCompleter.lastWordOf(text));
  // if the name has no searchable word, nothing can answer it
  if (completedQuery.terms.length === 0) {
    return null;
  }
  const foodWords = scannedFoodWords(text);

  const [deviceFoods, serverFoods, serverFoodsByFoodWords] = await Promise.all([
    deps.searchDevice(text),
    deps.searchServer(text, RANKED_SERVER_RESULTS),
    // if the food words are the whole name, the first search already asked for them
    foodWords && foodWords !== text.toLowerCase() ? deps.searchServer(foodWords, RANKED_SERVER_RESULTS) : Promise.resolve([]),
  ]);
  // one pool, each food once
  const pool: FoodHeader[] = [];
  const seen = new Set<string>();
  for (const food of [...serverFoods, ...serverFoodsByFoodWords]) {
    if (!seen.has(food._id)) {
      seen.add(food._id);
      pool.push(food);
    }
  }

  // THE STAPLES, only when the server gave nothing at all. Here rather than inside `searchDevice`
  // because that runs alongside the server searches and cannot know their outcome yet; see
  // `stapleFallbackHeaders`. An ONLINE scan is therefore completely unchanged.
  const deviceAndStaples = pool.length === 0
    ? withStapleFallback(deviceFoods, deps.stapleFallbackHeaders?.() ?? [])
    : deviceFoods;

  const ranked = rankFoodHeaders([...pool, ...deviceAndStaples], {
    now: deps.now,
    text,
    completedQuery,
    analyzerDeps: scanWordCompleter.analyzerDeps,
    rankBySourceTiers: true,
    isBarcodeSearch: false,
    boostRecentLogMeals: false,
    debugDescriptions: false,
    preparedWords: deps.stapleWords,
  });

  // the rule itself is shared with the server (#5623); the pick is its first candidate
  return rankScannedFoodCandidates(text, completedQuery.terms, ranked)[0] ?? null;
}
